<template>
    <div v-if="INTEGRATION">
        <Hero :left="true" v-if="INTEGRATION"
            :title="`${INTEGRATION.name.replace(/ /g, '&nbsp;')} API integration & MCP server`" colour-index="0"
            :image_url="INTEGRATION.type === 'bullhorn' ? undefined : INTEGRATION.logo_url" :subtitle="subtitle"
            :footer-items="[
                'Try free for 30 days, no credit card required',
                `Add ${INTEGRATION.name} and ${relatedIntegrationsCount} other pre-built ${primaryCategory.name} integrations`
            ]" :buttons="[
                INTEGRATION.in_progress
                    ? {
                        link: `/contact?c=${INTEGRATION.categories.filter((c) => c !== 'passthrough')[0]}&i=${INTEGRATION.type}`,
                        title: 'Request this integration',
                    }
                    : { link: 'https://app.unified.to', title: `Add ${INTEGRATION.name} to your app` },
                {
                    link: `https://docs.unified.to/${primaryCategory.key}/integrations`, title: 'See feature support', icon: 'ic:round-keyboard-arrow-right'
                }
            ]" />

        <div v-if="INTEGRATION.type === 'bullhorn'" class="px-3 sm:px-28 sm:mt-12 text-xs">
            Please note that Unified.to is not affilated with Bullhorn.
        </div>

        <section class="px-3 sm:px-28 my-20">
            <UseCases :title="`Real-time data to fuel countless ${primaryCategory.name?.split('&')[0]} use cases`"
                subtitle="Power AI copilots, insights, predictive analysis, and automation with a modern integration infrastructure that scales alongside you."
                :usecases="filteredUseCases" :max-items="3" />
        </section>

        <IntegrationDiagram :logo-src="INTEGRATION.logo_url!" :center-label="INTEGRATION.name"
            :items="filteredSupportObjects.map(item => ({ label: formatObjectName(item.key), href: item.key === 'passthrough' ? `https://docs.unified.to/passthrough/overview` : `https://docs.unified.to/${item.key.split('_')[0]?.toLowerCase()}/${item.key.split('_')[1]?.toLowerCase()}/model` }))"
            :scroll-seconds="10" :border="false" />

        <!-- Unified API support (ie. objects, and methods for each object) -->
        <section class="px-3 sm:px-28 my-20">
            <h3 class="mb-8 text-center font-semibold">
                Unified API data-models and methods
            </h3>
            <div class="flex flex-wrap">
                <div v-for="item in filteredSupportObjects" :key="item.key" class="sm:pr-2 pb-2 w-full"
                    :class="filteredSupportObjects.length < 3 ? 'sm:w-1/2' : '2xl:w-1/4 sm:w-1/3'">
                    <div class="border rounded-md h-full">
                        <div
                            class="bg-transparent px-4 py-4 text-md border-b rounded-t-md items-center text-light-gray flex justify-between">
                            <span class="font-semibold">{{ formatObjectName(item.key) }}</span>
                            <a target="_blank"
                                :href="item.key === 'passthrough' ? `https://docs.unified.to/passthrough/overview` : `https://docs.unified.to/${item.key.split('_')[0]?.toLowerCase()}/${item.key.split('_')[1]?.toLowerCase()}/model`">
                                <Icon name="ic:round-keyboard-arrow-right" />
                            </a>
                        </div>
                        <div class="px-4 py-4">
                            <div class="flex flex-wrap">
                                <Badge v-for="method in getSupportedMethods(item.object)" :key="method" :label="method"
                                    class="mr-1 mb-1" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- Unified MCP: loads the static /mcp-tools/<type>.json written by `npm run getmcptools` -->
        <section class="px-3 sm:px-28 my-20">
            <MCP :type="type" />
        </section>

        <Modern />

        <Benefits class="my-20"
            :title="`Add ${getArticle(INTEGRATION.name)} ${INTEGRATION.name} integration to your application in hours`"
            label bottom_image :benefits="[
                {
                    title: 'Test drive integrations',
                    body: `Effortlessly test API calls, explore endpoints, and try out the ${INTEGRATION.name} integration using synthetic data in our Sandbox environment. `,
                    image_url: '/images/user-flow-1.png'
                },
                {
                    title: 'Authorize connections',
                    body: `Drop in our Authorization component with a single line of code. Your users can then connect and grant access to their ${INTEGRATION.name} data.`,
                    image_url: '/images/user-flow-2.png'
                },
                {
                    title: 'Start making API calls',
                    body: `Use the Connection ID to interact with ${INTEGRATION.name} via our unified API. We handle maintenance and updates so you can focus on your product.`,
                    image_url: '/images/user-flow-3.png'
                },
            ]" />

        <SeamlessAuth v-if="primaryCategory.key !== 'auth'" />

        <section class="mt-20 -mb-32">
            <ClientOnly>
                <Customers
                    title="Trusted by global product companies and developers to power integrations for innovative AI and SaaS applications" />
            </ClientOnly>
        </section>

        <ClientOnly>
            <Testimonials :delay="6000" :limit="6" title="" />
        </ClientOnly>

        <section class="max-w-screen-2xl mx-auto w-full sm:px-28 my-20">
            <h3 class="mb-4">Integrate with all the major {{ primaryCategory.name?.toLowerCase() }} applications</h3>

            <p class="mb-4 text-neutral-200 sm:w-3/4">
                Unified.to makes it possible to integrate once to a unified API to connect with all the applications
                your customers use,
                so you can train AI models, build advanced automation, and expand your market.
            </p>

            <a href="https://app.unified.to" class="text-blue-500 flex items-center gap-2">
                Start testing for free
                <Icon name="ic:round-keyboard-arrow-right" />
            </a>

            <div class="mt-8">
                <IntegrationsList
                    :cat="(primaryCategory.key === 'scim' ? 'hris' : primaryCategory.key) as TIntegrationCategory"
                    :integrations="relatedIntegrationCards" />
            </div>
        </section>

        <Hero :title="heroTitle || 'Integrate once, launch in days'" heading-level="h2" :buttons="[
            { link: 'https://app.unified.to', title: 'Start free trial' },
            { link: '/contact', title: 'Book a product demo', icon: 'ic:round-keyboard-arrow-right' },
        ]" :subtitle="`With ${totalIntegrationsCount}+ pre-built integrations and zero maintenance burden, Unified.to unlocks your team's integration potential, so you can unblock growth.`"
            :highlight-words="['integrate']" />
    </div>
</template>

<script lang="ts">
import { CAT_SECTIONS } from '~~/data/sections';
import { getArticle, capitalize } from '~~/services/common';
import { type IIntegration, type IIntegrationSupport, type TIntegrationCategory } from '~/models/Unified';
import { FUTURE_INTEGRATIONS } from '~/services/integrations';
import { MAP_CATS } from '~~/services/categories';
import { INTEGRATIONS } from '~/services/integrations';
import { computed } from 'vue';
import { USE_CASES } from '~/data/use_cases';
import { integrationHeroVariants } from '~/data/experiments';

export default {

    setup() {
        const route = useRoute();
        const type = route.params.type as string;
        const _i = INTEGRATIONS.find((i) => i.type === type);
        const _f = FUTURE_INTEGRATIONS.find((i) => i.type === type);
        const integration = _i || (_f as IIntegration);

        if (!integration) {
            throw createError({ statusCode: 404, statusMessage: 'Integration not found' });
        }

        const filteredCats = integration.categories?.filter(
            (c) => c !== 'hris' && c !== 'ats' && c !== 'passthrough' && c !== 'auth'
        );
        const categories = filteredCats?.length ? filteredCats : integration.categories || [];

        const control = {
            variant: 'control',
            title: 'Integrate once, launch in days'
        }

        const { heroTitle } = useGetVariant("integration-page-hero", integrationHeroVariants, control)

        const subtitle = computed(() => {
            if (!integration) return '';
            const firstCategory = integration.categories.filter(c => c !== 'passthrough' && c !== 'metadata')[0];
            const categoryName = MAP_CATS[firstCategory as keyof typeof MAP_CATS] || firstCategory || '';
            return `Deliver real-time ${categoryName} faster, close sales deals sooner, and scale without an integration backlog.`
        });

        const primaryCategory = computed(() => {
            if (!integration?.categories) return { key: 'customer', name: 'Customer' };
            const category = integration.categories[0];
            // console.log(category)
            return {
                key: category,
                name: category && category in MAP_CATS ? MAP_CATS[category as keyof typeof MAP_CATS] : category
            };
        });

        const relatedIntegrationCards = computed(() => {
            if (!primaryCategory.value) return [];
            return INTEGRATIONS
                .filter(i =>
                    i.type !== integration?.type && // exclude current integration
                    i.categories.includes(primaryCategory.value.key as TIntegrationCategory) // match any category tag
                )
                .map((i) => ({
                    type: i.type,
                    name: i.name,
                    logo_url: i.logo_url,
                    fa_icon: i.fa_icon,
                    categories: i.categories,
                    recent: i.recent,
                    created_at: i.created_at,
                }));
        });

        const relatedIntegrationsCount = computed(() => {
            return relatedIntegrationCards.value.length;
        });



        const filteredUseCases = computed(() => {
            return USE_CASES.filter(useCase => useCase.category === primaryCategory.value.key);
        });

        const totalIntegrationsCount = computed(() => {
            return INTEGRATIONS.length;
        });

        useSEO({
            title: `Unified API for ${integration?.name} | Unified.to`,
            description: `Build ${integration?.name} integrations faster with our Unified API. Authentication experience, instant logging, webhook syncing, and choice of API tech including REST, GraphQL, and gRPC. `,
        });
        return {
            INTEGRATION: integration,
            type,
            MAP_CATS,
            CAT_SECTIONS,
            categories,
            subtitle,
            primaryCategory,
            relatedIntegrationsCount,
            relatedIntegrationCards,
            filteredUseCases,
            getArticle,
            totalIntegrationsCount,
            heroTitle,
        }
    },
    methods: {
        formatObjectName(objectKey: string): string {
            // Format object keys like "crm_contact" to "CRM Contact"
            const parts = objectKey.split('_');
            if (parts.length === 1 || parts[0] === parts[1]) {
                return capitalize(parts[0]!);
            }
            return parts[0]?.toUpperCase() + ' ' + capitalize(parts[1]!);
        },
        getSupportedMethods(object: IIntegrationSupport): string[] {
            if (object.methods?.callPassthrough === true) {
                return ['get', 'post', 'put', 'patch', 'delete'];
            };

            if (!object?.methods) return [];
            return Object.keys(object.methods).filter(method => object.methods![method]! === true);
        },
    },
    computed: {
        filteredSupportObjects(): Array<{ key: string; object: any }> {
            if (!this.INTEGRATION?.support) return [];
            const filtered: Array<{ key: string; object: any }> = [];
            Object.keys(this.INTEGRATION.support).forEach((key) => {
                if (key === 'passthrough') {
                    filtered.push({ key, object: this.INTEGRATION.support![key] });
                    return;
                };
                if (this.INTEGRATION.support?.[key]?.methods) {
                    const hasSupportedMethods = Object.keys(this.INTEGRATION.support[key]?.methods || {}).some(
                        (method) => this.INTEGRATION.support?.[key]?.methods?.[method]
                    );
                    if (hasSupportedMethods) {
                        filtered.push({ key, object: this.INTEGRATION.support[key] });
                    }
                }
            });
            return filtered;
        }
    },
};
</script>